import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Modal, Image } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

export default function Profile() {
  const [profile, setProfile] = useState({
    username: '',
    displayName: '',
    bio: '',
    location: '',
    profilePicture: '',
    backgroundImage: '',
    followersCount: 0,
    followingCount: 0,
    cardsCount: 0
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageType, setImageType] = useState(''); // 'profile' or 'background'
  const [hasProfile, setHasProfile] = useState(false);
  const [cardStats, setCardStats] = useState(null);
  
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      // Get user profile
      const { user } = await api.getUserProfile(currentUser.username);
      setProfile({
        username: user.username,
        displayName: user.displayName || user.username,
        bio: user.bio || '',
        location: user.location || '',
        profilePicture: user.profilePicture || '',
        backgroundImage: user.backgroundImage || '',
        followersCount: user.followersCount || 0,
        followingCount: user.followingCount || 0,
        cardsCount: 0
      });
      setHasProfile(true);
      
      // Get card stats
      const stats = await api.getCardStats();
      setCardStats(stats);
      setProfile(prev => ({ ...prev, cardsCount: stats.totalCards || 0 }));
      
    } catch (error) {
      console.error('Error loading profile:', error);
      setError('Failed to load profile');
      // If user doesn't have a profile yet, show editing mode
      if (error.message.includes('not found')) {
        setIsEditing(true);
        setHasProfile(false);
      }
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadUserProfile();
    }
  }, [currentUser, loadUserProfile]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await api.updateProfile({
        displayName: profile.displayName,
        bio: profile.bio,
        location: profile.location
      });
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      setHasProfile(true);
    } catch (error) {
      setError('Failed to update profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (file) => {
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      let imageUrl;
      if (imageType === 'profile') {
        const response = await api.uploadProfilePicture(file);
        imageUrl = response.imageUrl;
      } else {
        const response = await api.uploadBackgroundImage(file);
        imageUrl = response.imageUrl;
      }
      
      setProfile(prev => ({
        ...prev,
        [imageType === 'profile' ? 'profilePicture' : 'backgroundImage']: imageUrl
      }));
      
      setSuccess(`${imageType === 'profile' ? 'Profile' : 'Background'} image updated successfully!`);
      setShowImageModal(false);
    } catch (error) {
      setError('Failed to upload image: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (type) => {
    setImageType(type);
    setShowImageModal(true);
  };

  if (loading && !hasProfile) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      {/* Background Image Section */}
      <Row>
        <Col xs={12} className="p-0">
          <div 
            className="position-relative" 
            style={{ 
              height: '300px', 
              backgroundImage: profile.backgroundImage ? `url(${api.getImageUrl(profile.backgroundImage)})` : 'linear-gradient(to right, #1a1d29, #2d3343)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {isEditing && (
              <Button
                variant="light"
                size="sm"
                className="position-absolute top-0 end-0 m-3"
                onClick={() => openImageModal('background')}
              >
                <i className="fas fa-camera me-2"></i>
                Change Background
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Profile Content */}
      <Container className="mt-n5 position-relative">
        <Row>
          <Col lg={8} className="mx-auto">
            <Card className="shadow-lg border-0" style={{ backgroundColor: '#1a1d29' }}>
              <Card.Body className="p-4">
                {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
                {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
                
                <Row>
                  <Col md={3} className="text-center">
                    {/* Profile Picture */}
                    <div className="position-relative d-inline-block">
                      {profile.profilePicture ? (
                        <Image
                          src={api.getImageUrl(profile.profilePicture)}
                          roundedCircle
                          className="border border-4 border-light"
                          style={{ width: '150px', height: '150px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div 
                          className="rounded-circle border border-4 border-light d-flex align-items-center justify-content-center"
                          style={{ 
                            width: '150px', 
                            height: '150px', 
                            backgroundColor: '#4f9eff',
                            color: '#fff',
                            fontSize: '48px',
                            fontWeight: 'bold'
                          }}
                        >
                          {profile.username ? profile.username.charAt(0).toUpperCase() : 'U'}
                        </div>
                      )}
                      {isEditing && (
                        <Button
                          variant="primary"
                          size="sm"
                          className="position-absolute bottom-0 end-0 rounded-circle"
                          onClick={() => openImageModal('profile')}
                          style={{ width: '40px', height: '40px' }}
                        >
                          <i className="fas fa-camera"></i>
                        </Button>
                      )}
                    </div>
                    
                    {/* Stats */}
                    <Row className="mt-3 text-center">
                      <Col xs={4}>
                        <h5 className="mb-0 text-white">{profile.cardsCount}</h5>
                        <small className="text-muted">Cards</small>
                      </Col>
                      <Col xs={4}>
                        <h5 className="mb-0 text-white">{profile.followersCount}</h5>
                        <small className="text-muted">Followers</small>
                      </Col>
                      <Col xs={4}>
                        <h5 className="mb-0 text-white">{profile.followingCount}</h5>
                        <small className="text-muted">Following</small>
                      </Col>
                    </Row>
                  </Col>
                  
                  <Col md={9}>
                    {isEditing ? (
                      <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-light">Display Name</Form.Label>
                          <Form.Control
                            type="text"
                            value={profile.displayName}
                            onChange={(e) => setProfile({...profile, displayName: e.target.value})}
                            placeholder="Your display name"
                            style={{ backgroundColor: '#2d3343', border: '1px solid #4f9eff', color: '#fff' }}
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label className="text-light">Bio</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={profile.bio}
                            onChange={(e) => setProfile({...profile, bio: e.target.value})}
                            placeholder="Tell us about yourself..."
                            style={{ backgroundColor: '#2d3343', border: '1px solid #4f9eff', color: '#fff' }}
                          />
                        </Form.Group>
                        
                        <Form.Group className="mb-3">
                          <Form.Label className="text-light">Location</Form.Label>
                          <Form.Control
                            type="text"
                            value={profile.location}
                            onChange={(e) => setProfile({...profile, location: e.target.value})}
                            placeholder="City, State"
                            style={{ backgroundColor: '#2d3343', border: '1px solid #4f9eff', color: '#fff' }}
                          />
                        </Form.Group>
                        
                        <div className="d-flex gap-2">
                          <Button variant="primary" type="submit" disabled={loading}>
                            Save Profile
                          </Button>
                          {hasProfile && (
                            <Button variant="secondary" onClick={() => setIsEditing(false)}>
                              Cancel
                            </Button>
                          )}
                        </div>
                      </Form>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h3 className="text-white mb-0">{profile.displayName || profile.username}</h3>
                            <p className="text-muted">@{profile.username}</p>
                          </div>
                          <Button variant="outline-primary" onClick={() => setIsEditing(true)}>
                            Edit Profile
                          </Button>
                        </div>
                        
                        {profile.bio && (
                          <p className="text-light mt-3">{profile.bio}</p>
                        )}
                        
                        {profile.location && (
                          <p className="text-muted">
                            <i className="fas fa-map-marker-alt me-2"></i>
                            {profile.location}
                          </p>
                        )}
                        
                        <Button 
                          variant="primary" 
                          className="mt-3"
                          onClick={() => navigate(`/${profile.username}`)}
                        >
                          View Public Profile
                        </Button>
                      </>
                    )}
                  </Col>
                </Row>
                
                {/* Collection Stats */}
                {cardStats && !isEditing && (
                  <Row className="mt-4">
                    <Col>
                      <h5 className="text-white mb-3">Collection Overview</h5>
                      <Row>
                        <Col md={3}>
                          <Card bg="dark" text="white" className="mb-3">
                            <Card.Body className="text-center">
                              <h4>{cardStats.totalCards}</h4>
                              <small>Total Cards</small>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={3}>
                          <Card bg="dark" text="white" className="mb-3">
                            <Card.Body className="text-center">
                              <h4>${cardStats.totalValue.toFixed(2)}</h4>
                              <small>Total Value</small>
                            </Card.Body>
                          </Card>
                        </Col>
                        <Col md={6}>
                          <Card bg="dark" text="white" className="mb-3">
                            <Card.Body>
                              <h6>Categories</h6>
                              {Object.entries(cardStats.categoryCounts).map(([category, count]) => (
                                <div key={category} className="d-flex justify-content-between">
                                  <span>{category}</span>
                                  <span>{count}</span>
                                </div>
                              ))}
                            </Card.Body>
                          </Card>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Image Upload Modal */}
      <Modal show={showImageModal} onHide={() => setShowImageModal(false)}>
        <Modal.Header closeButton style={{ backgroundColor: '#1a1d29', color: '#fff' }}>
          <Modal.Title>Upload {imageType === 'profile' ? 'Profile' : 'Background'} Image</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#2d3343', color: '#fff' }}>
          <Form>
            <Form.Group>
              <Form.Label>Choose an image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e.target.files[0])}
                disabled={loading}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
}